
import { useState, useMemo } from "react";
import { subDays, subMonths, subYears, startOfDay, endOfDay } from "date-fns";

export interface DateRange {
  from: Date;
  to: Date;
}

export const useDateRangeFilter = () => {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subMonths(new Date(), 6), // Default to last 6 months
    to: new Date()
  });

  const presetRanges = useMemo(() => [
    {
      label: "Last 7 days",
      value: "7d",
      range: {
        from: subDays(new Date(), 7),
        to: new Date()
      }
    },
    {
      label: "Last 30 days", 
      value: "30d",
      range: {
        from: subDays(new Date(), 30),
        to: new Date()
      }
    },
    {
      label: "Last 3 months",
      value: "3m",
      range: {
        from: subMonths(new Date(), 3),
        to: new Date()
      }
    },
    {
      label: "Last 6 months",
      value: "6m", 
      range: {
        from: subMonths(new Date(), 6),
        to: new Date()
      }
    },
    {
      label: "Last year",
      value: "1y",
      range: {
        from: subYears(new Date(), 1),
        to: new Date()
      }
    },
    {
      label: "All time",
      value: "all",
      range: {
        from: new Date(2020, 0, 1), // Reasonable start date
        to: new Date()
      }
    }
  ], []);

  const setPresetRange = (presetValue: string) => {
    const preset = presetRanges.find(p => p.value === presetValue);
    if (preset) {
      setDateRange(preset.range);
    }
  };

  const filterDataByDateRange = <T extends { created_at?: string | null }>(data: T[]): T[] => {
    if (!data || data.length === 0) return [];
    
    const fromDate = startOfDay(dateRange.from);
    const toDate = endOfDay(dateRange.to);
    
    return data.filter(item => {
      if (!item.created_at) return false;
      const itemDate = new Date(item.created_at);
      return itemDate >= fromDate && itemDate <= toDate;
    });
  };

  return {
    dateRange,
    setDateRange,
    presetRanges,
    setPresetRange,
    filterDataByDateRange
  };
};
